import { ContentEdit, ContentDownload, MenuClose } from '@fs/zion-icon'
import { Button, Radio, RadioGroup, Row, Select, Separator, useOverlay, H3, H4 } from '@fs/zion-ui'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import useNotifications from '../../../hooks/useNotifications'
import useTempleDirectory from '../../../hooks/useTempleDirectory'
import { putOwsSummary } from '../../../services/owsService'
import {
  getIdElement,
  getLastPathElement,
  getNumericalIdFromId,
  getPidFromIdentifier,
  setIdInIdentifiers,
} from '../../../util/StringUtil'
import { deepCopy } from '../../../util/Utils'
import { PersonLink, RequestLink } from '../../app/Links'
import { PermissionsContext } from '../../app/PermissionsContext'
import DmcDataView from '../../common/DmcDataView'
import Editable from '../../common/Editable'
import { OwsContext } from '../OwsContext'
import RecordOrdinanceOverlay from './RecordOrdinanceOverlay'
import OwsCtrList from './OwsCtrList'
import OwsDeletedInfo from './OwsDeletedInfo'
import OwsPrintDateList from './OwsPrintDateList'
import OwsProxyHeirList from './OwsProxyHeirList'
import OwsSaveChangesDialog from './OwsSaveChangesDialog'
import CtrOwsSummaryPerson from '../../common/temple/CtrOwsSummaryPerson'
import AddPerson from '../../common/temple/AddPerson'
import { usePageTitle } from '../../app/PageTitleContext'

const personTypes = {
  FATHER: 'ORD_FATHER',
  MOTHER: 'ORD_MOTHER',
  PRINCIPAL: 'ORD_PRINCIPAL',
  SPOUSE: 'ORD_SPOUSE',
}

export default function OwsSummary() {
  const { t } = useTranslation()
  usePageView({ page_detail: 'DMConsole:OWSSummary' })
  usePageTitle(t('pageTitle.ows.summary'))
  const saveChangesDialogOpener = useOverlay()
  const recordOrdinanceOverlay = useOverlay()
  const { temples } = useTempleDirectory()
  const owsContext = useContext(OwsContext)
  const { isRole } = useContext(PermissionsContext)
  const { error } = useNotifications()
  const currentOws = owsContext.currentOws
  const [editableOws, setEditableOws] = useState(JSON.parse(JSON.stringify(currentOws)))
  const [isEditing, setEditing] = useState(false)
  const [verifiedPersons, setVerifiedPersons] = useState({})
  const [verifiedMembers, setVerifiedMembers] = useState({})
  const setEditableTemplePlatform = (templePlatform) =>
    setEditableOws((prev) => {
      return { ...prev, templePlatform }
    })
  const setEditableCtrExtendedDetail = (ows) =>
    setEditableOws((prev) => {
      return { ...prev, templePlatform: { ...prev.templePlatform, ordinanceWorkSets: [ows] } }
    })

  const determineDisableSaveBtn = () => {
    if (Object.values(verifiedPersons).some((verifiedPerson) => !verifiedPerson)) {
      return true
    }
    if (Object.values(verifiedMembers).some((verifiedMember) => !verifiedMember)) {
      return true
    }
    const editableOwsCopy = deepCopy(editableOws)
    editableOwsCopy.templePlatform.persons.forEach((person) => {
      person?.facts?.forEach((fact) => delete fact.dateItem)
    })
    if (JSON.stringify(editableOwsCopy) !== JSON.stringify(currentOws)) {
      editableOwsCopy.templePlatform.persons.forEach((person) => {
        if (person.id.includes('qr.')) {
          const pid = getPidFromIdentifier(person.identifiers)
          if (pid) {
            setIdInIdentifiers(
              person.identifiers,
              `p.${getIdElement(pid)}`,
              'http://api.familysearch.org/temple/USYS_ID'
            )
          }
        }
      })
      if (JSON.stringify(currentOws) !== JSON.stringify(editableOwsCopy)) {
        return false
      }
    }
    return true
  }

  const reset = () => {
    setEditing(false)
    setEditableOws(JSON.parse(JSON.stringify(currentOws)))
    setVerifiedPersons({})
    setVerifiedMembers({})
  }

  const saveChanges = async (reasonText, reasonType) => {
    saveChangesDialogOpener.close()
    putOwsSummary(editableOws, reasonText, reasonType)
      .then(() => {
        owsContext.fetchCurrentOws()
      })
      .catch((err) => {
        error(
          t('ows.errors.editingOws', {
            defaultValue: `Error editing OWS: ${err.message || ''}`,
            message: err.message || '',
          }),
          err
        )
      })
  }

  const updateSubmissionType = (e) => {
    const editableOwsCopy = JSON.parse(JSON.stringify(editableOws))
    editableOwsCopy.templePlatform.ordinanceWorkSets[0].submissionType = e.target.value
    setEditableOws(editableOwsCopy)
  }

  const updatePaperType = (e) => {
    const editableOwsCopy = JSON.parse(JSON.stringify(editableOws))
    editableOwsCopy.templePlatform.ordinanceWorkSets[0].paperType = e.target.value
    setEditableOws(editableOwsCopy)
  }

  const extractQualifiedOrdinances = () => {
    let qualifiedOrdinances = ''
    if (
      currentOws.templePlatform.ordinanceWorkSets[0].qualifiedOrdinances &&
      currentOws.templePlatform.ordinanceWorkSets[0].qualifiedOrdinances.length > 0
    ) {
      currentOws.templePlatform.ordinanceWorkSets[0].qualifiedOrdinances.forEach((qualifiedOrdinance) => {
        qualifiedOrdinances = `${qualifiedOrdinances + getLastPathElement(qualifiedOrdinance)},`
      })
      qualifiedOrdinances = qualifiedOrdinances.trim().slice(0, -1)
    }
    return qualifiedOrdinances
  }

  const idsToOmit = currentOws.templePlatform.persons.map(
    (person) => person.identifiers?.['http://api.familysearch.org/temple/USYS_ID']?.[0]?.split('.')[1]
  )
  const getPersonsWithOrdinanceReferences = (personType) => {
    return (
      currentOws.tfEntityRefReverseLookupList &&
      currentOws.tfEntityRefReverseLookupList
        .filter((entityRef) => !idsToOmit.includes(entityRef.entityId))
        .filter((entityRef) => entityRef.entityRefNode.entityRef.value.role === personType)
        .map((entityRef, index, array) => {
          if (index === array.length - 1)
            return (
              <React.Fragment key={entityRef.entityId}>
                <PersonLink personId={entityRef.entityId} newTab />
              </React.Fragment>
            )
          return (
            <React.Fragment key={entityRef.entityId}>
              <PersonLink personId={entityRef.entityId} newTab />,{' '}
            </React.Fragment>
          )
        })
    )
  }

  const otherPrincipalRefs = getPersonsWithOrdinanceReferences(personTypes.PRINCIPAL)
  const otherFatherRefs = getPersonsWithOrdinanceReferences(personTypes.FATHER)
  const otherMotherRefs = getPersonsWithOrdinanceReferences(personTypes.MOTHER)
  const otherSpouseRefs = getPersonsWithOrdinanceReferences(personTypes.SPOUSE)
  const otherPersonsWithOrdinanceReferencesData = []
  if (otherPrincipalRefs?.length > 0)
    otherPersonsWithOrdinanceReferencesData.push({
      label: t('ows.otherPersons.principal', 'Principal'),
      detail: otherPrincipalRefs,
      key: 'other-principal-refs',
    })
  if (otherFatherRefs?.length > 0)
    otherPersonsWithOrdinanceReferencesData.push({
      label: t('ows.otherPersons.father', 'Father'),
      detail: otherFatherRefs,
      key: 'other-father-refs',
    })
  if (otherMotherRefs?.length > 0)
    otherPersonsWithOrdinanceReferencesData.push({
      label: t('ows.otherPersons.mother', 'Mother'),
      detail: otherMotherRefs,
      key: 'other-mother-refs',
    })
  if (otherSpouseRefs?.length > 0)
    otherPersonsWithOrdinanceReferencesData.push({
      label: t('ows.otherPersons.spouse', 'Spouse'),
      detail: otherSpouseRefs,
      key: 'other-spouse-refs',
    })

  if (!currentOws) {
    return null
  }
  const paperTypeText =
    getLastPathElement(editableOws.templePlatform.ordinanceWorkSets[0].paperType) === 'COUPLE'
      ? t(
          `${getLastPathElement(editableOws.templePlatform.ordinanceWorkSets[0].paperType)?.toLowerCase()}`,
          getLastPathElement(editableOws.templePlatform.ordinanceWorkSets[0].paperType)
        )
      : t(
          `gender.${getLastPathElement(editableOws.templePlatform.ordinanceWorkSets[0].paperType)?.toLowerCase()}`,
          getLastPathElement(editableOws.templePlatform.ordinanceWorkSets[0].paperType)
        )
  const owsFields = [
    {
      label: t('osid', 'OSID'),
      detail: currentOws.templePlatform.ordinanceWorkSets[0].osid
        ? currentOws.templePlatform.ordinanceWorkSets[0].osid
        : '',
      key: 'detail-osid',
    },
    {
      label: t('printed', 'Printed'),
      detail:
        currentOws.templePlatform.ordinanceWorkSets[0].beenPrinted !== null
          ? currentOws.templePlatform.ordinanceWorkSets[0].beenPrinted.toString()
          : '',
      key: 'detail-printed',
    },
    {
      label: t('temple.request.requestId', 'Request ID'),
      detail: currentOws.templePlatform.ordinanceWorkSets[0].request?.resourceId && (
        <>
          <RequestLink requestId={currentOws.templePlatform.ordinanceWorkSets[0].request.resourceId} /> (
          <RequestLink
            requestId={getNumericalIdFromId(currentOws.templePlatform.ordinanceWorkSets[0].request.resourceId)}
          />
          )
        </>
      ),
      key: 'detail-requestId',
    },
    {
      label: t('created', 'Created'),
      detail: currentOws.templePlatform.ordinanceWorkSets[0].createDate.original
        ? currentOws.templePlatform.ordinanceWorkSets[0].createDate.original
        : '',
      key: 'detail-created',
    },
    {
      label: t('ows.paperType', 'Paper Type'),
      detail: (
        <Editable text={paperTypeText} isEdit={isEditing}>
          <RadioGroup orientation="horizontal">
            <Radio
              checked={getLastPathElement(editableOws.templePlatform.ordinanceWorkSets[0].paperType) === 'MALE'}
              label={t('gender.male', 'Male')}
              name="Male"
              value="http://api.familysearch.org/temple/MALE"
              onChange={updatePaperType}
            />
            <Radio
              checked={getLastPathElement(editableOws.templePlatform.ordinanceWorkSets[0].paperType) === 'FEMALE'}
              label={t('gender.female', 'Female')}
              name="Female"
              value="http://api.familysearch.org/temple/FEMALE"
              onChange={updatePaperType}
            />
            <Radio
              checked={getLastPathElement(editableOws.templePlatform.ordinanceWorkSets[0].paperType) === 'COUPLE'}
              label={t('couple', 'Couple')}
              name="Couple"
              value="http://api.familysearch.org/temple/COUPLE"
              onChange={updatePaperType}
            />
          </RadioGroup>
        </Editable>
      ),
      key: 'detail-paperType',
    },
    {
      label: t('duplicate', 'Duplicate'),
      detail:
        currentOws.templePlatform.ordinanceWorkSets[0].duplicate !== null
          ? currentOws.templePlatform.ordinanceWorkSets[0].duplicate.toString()
          : '',
      key: 'detail-duplicate',
    },
    {
      label: t('updated', 'Updated'),
      detail: currentOws.templePlatform.ordinanceWorkSets[0].updateDate.original
        ? currentOws.templePlatform.ordinanceWorkSets[0].updateDate.original
        : '',
      key: 'detail-updated',
    },
    {
      label: t('ows.submissionType', 'Submission Type'),
      detail: (
        <Editable
          text={t(
            `ows.submissionTypes.${getLastPathElement(editableOws.templePlatform.ordinanceWorkSets[0].submissionType)}`,
            getLastPathElement(editableOws.templePlatform.ordinanceWorkSets[0].submissionType)
          )}
          isEdit={isEditing}
        >
          <div style={{ display: 'inline-block' }}>
            <Select
              id="Submission Type"
              value={editableOws.templePlatform.ordinanceWorkSets[0].submissionType}
              onChange={updateSubmissionType}
              options={[
                {
                  value: 'http://api.familysearch.org/temple/LIVING',
                  label: t('ows.submissionTypes.LIVING', 'Living'),
                },
                {
                  value: 'http://api.familysearch.org/temple/PATRON_SUBMITTED',
                  label: t('ows.submissionTypes.PATRON_SUBMITTED', 'Patron Submitted'),
                },
                {
                  value: 'http://api.familysearch.org/temple/EXTRACTION',
                  label: t('ows.submissionTypes.EXTRACTION', 'Extraction'),
                },
                {
                  value: 'http://api.familysearch.org/temple/FAMILY_CARD',
                  label: t('ows.submissionTypes.FAMILY_CARD', 'Family Card'),
                },
                {
                  value: 'DECEASED_MEMBER_FILE',
                  label: t('ows.submissionTypes.DECEASED_MEMBER_FILE', 'Deceased Member File'),
                },
                {
                  value: 'FAMILY_ORDINANCE_SUMMARY',
                  label: t('ows.submissionTypes.FAMILY_ORDINANCE_SUMMARY', 'Family Ordinance Summary'),
                },
              ]}
            />
          </div>
        </Editable>
      ),
      key: 'detail-submissionType',
    },
  ]

  return (
    <>
      <OwsSaveChangesDialog overlay={saveChangesDialogOpener} saveChanges={saveChanges} />
      <RecordOrdinanceOverlay overlay={recordOrdinanceOverlay} />
      <Row>
        <H3 size="H5" growX>
          {t('ows.owsDetails', 'OWS Details')}
        </H3>
        {isRole(['Data Admin 6', 'Russian Data Admin']) && (
          <Button emphasis="medium" onClick={recordOrdinanceOverlay.handleClick}>
            {t('ows.recordOrdinance', 'Record Ordinance')}
          </Button>
        )}
        {isEditing ? (
          <Row gutters="nano">
            <Button
              disabled={determineDisableSaveBtn()}
              Icon={ContentDownload}
              emphasis="high"
              onClick={saveChangesDialogOpener.handleClick}
            >
              {t('action.save', 'Save')}
            </Button>
            <Button Icon={MenuClose} emphasis="medium" onClick={() => reset()}>
              {t('action.cancel', 'Cancel')}
            </Button>
          </Row>
        ) : (
          <Button emphasis="medium" Icon={ContentEdit} onClick={() => setEditing(true)}>
            {t('action.edit', 'Edit')}
          </Button>
        )}
      </Row>
      <DmcDataView id="OWS Details" data={owsFields} />
      <DmcDataView
        id="OWS Qualified Ordinances"
        data={[
          {
            label: t('ows.qualifiedOrdinances', 'Qualified Ordinances'),
            detail: extractQualifiedOrdinances(),
            key: 'qualifiedOrdinances',
          },
        ]}
        columns={1}
      />
      <Separator size="nano" />
      {otherPersonsWithOrdinanceReferencesData.length > 0 && (
        <>
          <H4 size="H5" supplementary>
            {t('ows.otherPersons', 'Other Persons with Ordinance References')}
          </H4>
          <DmcDataView data={otherPersonsWithOrdinanceReferencesData} columns={1} />
        </>
      )}
      <Separator />
      <OwsDeletedInfo
        deletedDate={currentOws.templePlatform.ordinanceWorkSets[0].deletedDate}
        deleteChanges={currentOws.templePlatform.ordinanceWorkSets[0].owsDeleteChanges}
      />
      <OwsPrintDateList printDateList={currentOws.templePlatform.ordinanceWorkSets[0].printDates} temples={temples} />
      <OwsProxyHeirList proxyHeirList={currentOws.templePlatform.ordinanceWorkSets[0].proxyHeirs} />
      <OwsCtrList ctrList={currentOws.templePlatform.ctrs} temples={temples} />
      {editableOws.templePlatform.persons &&
        editableOws.templePlatform.persons
          .map((person, ogIndex) => {
            return { person, ogIndex }
          })
          .filter(({ person }) => !person.id.includes('qr.'))
          .map(({ person, ogIndex }) => {
            return (
              <CtrOwsSummaryPerson
                key={`${person.id}-ctr-person`}
                personDataId={person.id}
                personIndex={ogIndex}
                editableTemplePlatform={editableOws.templePlatform}
                setEditableTemplePlatform={setEditableTemplePlatform}
                editableCtrExtendedDetail={currentOws.templePlatform.ordinanceWorkSets[0]}
                setEditableCtrExtendedDetail={setEditableCtrExtendedDetail}
                isEditing={isEditing}
                setVerifiedPersons={setVerifiedPersons}
                setVerifiedMembers={setVerifiedMembers}
              />
            )
          })}
      {isEditing && (
        <AddPerson
          editableTemplePlatform={editableOws.templePlatform}
          setEditableTemplePlatform={setEditableTemplePlatform}
          editableCtrExtendedDetail={currentOws.templePlatform.ordinanceWorkSets[0]}
          setEditableCtrExtendedDetail={setEditableCtrExtendedDetail}
        />
      )}
    </>
  )
}
